import {FormEvent, useState} from "react";
import {Alert, Card, Form, ListGroup} from "react-bootstrap";
import {CheckoutApi} from "../../api/CheckoutApi";
import {ProductsApi} from "../../api/ProductsApi";
import {SiteConfigApi} from "../../api/SiteConfigApi";
import {CheckoutConfig, offeredProviders} from "../../api/models";
import {useAsync} from "../../components/useAsync";
import {useShop} from "../../components/ShopContext";
import {useSiteStatus} from "../../components/SiteStatusContext";
import {ConfirmModal} from "../../components/ConfirmModal";
import {ErrorAlert} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {RichTextEditor} from "../../components/RichTextEditor";
import {SubmitButton} from "../../components/SubmitButton";
import {home} from "../../i18n";
import {DEFAULT_PRESS_ABOUT} from "../press/PressPage";

// The site's switches, maintenance mode and the shop, the product the home page features, what the home page says
// while the shop is closed, and the press page's About Tristar. Turning either one towards customers' view (maintenance on, the
// shop open) asks first; turning it back doesn't, so it's quick in an emergency.
export function SiteSettingsPage() {
    return (
        <>
            <PageHeader title="Settings"/>
            <MaintenanceSettings/>
            <HomePageSettings/>
            <ShopSettings/>
            <PressPageSettings/>
        </>
    );
}

// On, anyone but an administrator, signed in or not, sees only the maintenance message; the API refuses their calls.
// Astrel (licenses, update checks) and the payment webhooks still work, and anyone can still sign in at /login.
function MaintenanceSettings() {
    const status = useSiteStatus();
    // Asked afresh here, rather than trusting what the site loaded with: another administrator may have flipped it since.
    const {data: config, error: loadError, loading, setData} = useAsync(async () => {
        const fresh = await SiteConfigApi.get();
        status.setMaintenance(fresh.maintenanceMode);
        return fresh;
    }, []);
    const [confirmOn, setConfirmOn] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();

    const setMaintenance = async (maintenanceMode: boolean) => {
        setBusy(true);
        setError(undefined);
        try {
            const fresh = await SiteConfigApi.set({ maintenanceMode });
            setData(fresh);
            status.setMaintenance(fresh.maintenanceMode);
            setConfirmOn(false);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="mb-5" aria-labelledby="maintenance-heading">
            <h2 id="maintenance-heading" className="h5">Maintenance</h2>
            {loading && !config && <LoadingSpinner/>}
            <ErrorAlert error={loadError ?? error}/>
            {config && (
                <Card className={config.maintenanceMode ? 'border-warning' : undefined}>
                    <Card.Body>
                        <Form.Check type="switch" role="switch" id="maintenance-mode" className="fs-5 mb-2"
                                    label="Maintenance mode" checked={config.maintenanceMode} disabled={busy}
                                    onChange={e => e.target.checked ? setConfirmOn(true) : setMaintenance(false)}/>
                        <p className="text-body-secondary mb-0">
                            {config.maintenanceMode
                                ? 'On: only administrators can use the site. Everyone else sees the maintenance message.'
                                : 'Off: the site is open to everyone.'}
                            {' '}Astrel's licenses and update checks, and payments already under way, work either way.
                        </p>
                    </Card.Body>
                </Card>
            )}
            <ConfirmModal show={confirmOn} title="Turn maintenance mode on?" confirmLabel="Turn it on" variant="warning" busy={busy}
                          onConfirm={() => setMaintenance(true)} onCancel={() => setConfirmOn(false)}>
                <p>Everyone but administrators will see the maintenance message, signed in or not.</p>
                <p className="mb-0">You can turn it off again here at any time. Deploying the site fails while it's on.</p>
            </ConfirmModal>
        </section>
    );
}

// The product the home page features in its hero (the others are cards below it). Chosen from every product, so one can
// be chosen before it's published; until it is, the first published by name stays featured. Saved as soon as it's chosen.
function HomePageSettings() {
    const {data, error: loadError, loading, setData} = useAsync(async () => {
        const [config, all] = await Promise.all([SiteConfigApi.get(), ProductsApi.getAll()]);
        return { featuredSku: config.featuredSku ?? '', products: all };
    }, []);
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<unknown>();

    const choose = async (featuredSku: string) => {
        setBusy(true);
        setSaved(false);
        setError(undefined);
        try {
            const fresh = await SiteConfigApi.set({ featuredSku });
            setData(current => current && { ...current, featuredSku: fresh.featuredSku ?? '' });
            setSaved(true);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    const published = data?.products.filter(p => p.published) ?? [];
    const firstByName = [...published].sort((a, b) => a.name.localeCompare(b.name))[0];
    const chosen = data?.products.find(p => p.sku === data.featuredSku);
    const featured = chosen?.published ? chosen : firstByName;

    return (
        <section className="mb-5" aria-labelledby="home-page-heading">
            <h2 id="home-page-heading" className="h5">Home page</h2>
            {loading && !data && <LoadingSpinner/>}
            <ErrorAlert error={loadError ?? error}/>
            {data && (
                <Card>
                    <Card.Body>
                        <Form.Label htmlFor="featured-product" className="fs-5">Featured product</Form.Label>
                        <p className="text-body-secondary">
                            Shown in the home page's hero, with its demos and closing panel. The other published products are cards below it.
                        </p>
                        {saved && <Alert variant="success" dismissible onClose={() => setSaved(false)}>Saved.</Alert>}
                        <Form.Select id="featured-product" value={data.featuredSku} disabled={busy} style={{ maxWidth: '32rem' }}
                                     onChange={e => choose(e.target.value)}>
                            <option value="">The first published product by name{firstByName ? ` (now ${firstByName.name})` : ''}</option>
                            {data.products.map(p => (
                                <option key={p.id} value={p.sku}>{p.name}{p.published ? '' : ' (not published yet)'}{p.beta ? ' · Early Access' : ''}</option>
                            ))}
                        </Form.Select>
                        <p className="small text-body-secondary mt-2 mb-0">
                            {chosen && !chosen.published
                                ? `${chosen.name} isn't published yet: until it is, the home page features ${firstByName?.name ?? 'no product'}.`
                                : featured ? `The home page features ${featured.name}.` : 'No product is published yet: the home page shows the logo.'}
                        </p>
                    </Card.Body>
                </Card>
            )}
        </section>
    );
}

// The shop's master switch. Closed, the home page shows the closed notice, and there's no Buy button, My orders
// or Orders, whatever payment providers are set up; the API starts no checkouts. Payments already under way still
// complete, and refunds still revoke licenses.
function ShopSettings() {
    const shop = useShop();
    // Asked afresh here, rather than trusting what the site loaded with: another administrator may have flipped it since.
    const {data: config, error: loadError, loading, setData} = useAsync(async () => {
        const fresh = await CheckoutApi.config();
        shop.setConfig(fresh);
        return fresh;
    }, []);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();

    const setEnabled = async (enabled: boolean) => {
        setBusy(true);
        setError(undefined);
        try {
            await SiteConfigApi.set({ ecommerceEnabled: enabled });
            const fresh = await CheckoutApi.config();
            setData(fresh);
            shop.setConfig(fresh);
            setConfirmOpen(false);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    const onSwitch = (enabled: boolean) => enabled ? setConfirmOpen(true) : setEnabled(false);
    const anyProvider = offeredProviders(config).length > 0;

    return (
        <section aria-labelledby="shop-heading">
            <h2 id="shop-heading" className="h5">Shop</h2>
            {loading && !config && <LoadingSpinner/>}
            <ErrorAlert error={loadError ?? error}/>
            {config && (
                <>
                    <Card className="mb-4">
                        <Card.Body>
                            <Form.Check type="switch" role="switch" id="shop-enabled" className="fs-5 mb-2"
                                        label="Open for business" checked={config.enabled} disabled={busy}
                                        onChange={e => onSwitch(e.target.checked)}/>
                            <p className="text-body-secondary mb-0">
                                {config.enabled
                                    ? 'Customers can buy licenses. The home page has no closed notice, and My orders and Orders are shown.'
                                    : 'Nothing is for sale: the home page shows the closed notice, and there\'s no Buy button, My orders or Orders. Payments already under way still complete.'}
                            </p>
                        </Card.Body>
                    </Card>
                    <ClosedNotice config={config} onSaved={fresh => { setData(fresh); shop.setConfig(fresh); }}/>
                    {config.enabled && !anyProvider && (
                        <Alert variant="warning">The shop is open, but no payment provider is set up, so there's still nothing to buy.</Alert>
                    )}
                    <h3 className="h6">Payment providers</h3>
                    <ListGroup className="mb-2" style={{ maxWidth: '32rem' }}>
                        <ProviderStatus name="Paddle (merchant of record: cards, PayPal and more)" ready={config.paddle}/>
                        <ProviderStatus name="Stripe (cards)" ready={config.stripe} note={config.paddle && config.stripe ? 'not offered while Paddle is set up' : undefined}/>
                        <ProviderStatus name="PayPal" ready={config.paypal}
                                        note={!config.stripe ? 'needs Stripe too, for its tax' : config.paddle && config.paypal ? 'not offered while Paddle is set up' : undefined}/>
                    </ListGroup>
                    <p className="small text-body-secondary">
                        Set up with the API's <code>scripts/set-payment-params.sh</code>, then redeploy the API.
                    </p>
                </>
            )}
            <ConfirmModal show={confirmOpen} title="Open the shop?" confirmLabel="Open the shop" variant="primary" busy={busy}
                          onConfirm={() => setEnabled(true)} onCancel={() => setConfirmOpen(false)}>
                <p>Customers will be able to buy licenses{anyProvider ? '' : ' (once a payment provider is set up)'}.</p>
                <p className="mb-0">You can close it again here at any time.</p>
            </ConfirmModal>
        </section>
    );
}

// home.closed's markup is a <strong> tag, so it's already the editor's HTML once in a paragraph.
const DEFAULT_CLOSED_NOTICE = `<p>${home.closed}</p>`;

// What the home page says while the shop is closed. Until an administrator saves one, it's the site's own wording (which
// the editor starts with); saved empty, there's no notice at all.
function ClosedNotice({config, onSaved}: { config: CheckoutConfig, onSaved(config: CheckoutConfig): void }) {
    const current = config.closedMessage ?? DEFAULT_CLOSED_NOTICE;
    const [message, setMessage] = useState(current);
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<unknown>();

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setSaved(false);
        setError(undefined);
        try {
            await SiteConfigApi.set({ closedMessage: message });
            onSaved(await CheckoutApi.config());
            setSaved(true);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card className="mb-4">
            <Card.Body>
                <Form onSubmit={submit}>
                    <Form.Label id="closed-notice-label" className="fs-5">Closed notice</Form.Label>
                    <p className="text-body-secondary">
                        Shown on the home page while the shop is closed. Leave it empty for no notice.
                    </p>
                    <ErrorAlert error={error} messages={{ 400: 'The notice is too long: keep it under 2,000 characters of HTML.' }}/>
                    {saved && <Alert variant="success" dismissible onClose={() => setSaved(false)}>Saved.</Alert>}
                    <RichTextEditor labelledBy="closed-notice-label" value={message} onChange={x => { setMessage(x); setSaved(false); }}/>
                    <div className="mt-3 d-flex align-items-center gap-3">
                        <SubmitButton busy={busy}>Save</SubmitButton>
                        {message !== current && <span className="small text-body-secondary">Unsaved changes</span>}
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
}

// The press page's About Tristar section. Saved empty, the page uses the site's own wording again (which the editor then
// starts from).
function PressPageSettings() {
    const {data: config, error: loadError, loading, setData} = useAsync(() => SiteConfigApi.get(), []);
    const current = config?.pressAbout?.trim() || DEFAULT_PRESS_ABOUT;
    const [about, setAbout] = useState<string>();
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<unknown>();
    const value = about ?? current;

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setSaved(false);
        setError(undefined);
        try {
            setData(await SiteConfigApi.set({ pressAbout: value.trim() }));
            setAbout(undefined);
            setSaved(true);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="mb-5" aria-labelledby="press-page-heading">
            <h2 id="press-page-heading" className="h5">Press page</h2>
            {loading && !config && <LoadingSpinner/>}
            <ErrorAlert error={loadError}/>
            {config && (
                <Card>
                    <Card.Body>
                        <Form onSubmit={submit}>
                            <Form.Label id="press-about-label" className="fs-5">About Tristar</Form.Label>
                            <p className="text-body-secondary">
                                The first section of <a href="/press" target="_blank" rel="noopener">the press page</a>, for journalists writing about the company.
                                Save it empty to go back to the site's own wording.
                            </p>
                            <ErrorAlert error={error} messages={{ 400: 'The text is too long: keep it under 10,000 characters of HTML.' }}/>
                            {saved && <Alert variant="success" dismissible onClose={() => setSaved(false)}>Saved.</Alert>}
                            <RichTextEditor labelledBy="press-about-label" value={value} onChange={x => { setAbout(x); setSaved(false); }}/>
                            <div className="mt-3 d-flex align-items-center gap-3">
                                <SubmitButton busy={busy}>Save About Tristar</SubmitButton>
                                {about !== undefined && about !== current && <span className="small text-body-secondary">Unsaved changes</span>}
                            </div>
                        </Form>
                    </Card.Body>
                </Card>
            )}
        </section>
    );
}

function ProviderStatus({name, ready, note}: { name: string, ready: boolean, note?: string }) {
    return (
        <ListGroup.Item className="d-flex justify-content-between align-items-center">
            <span>{name}{note && <span className="small text-body-secondary"> ({note})</span>}</span>
            {ready
                ? <span className="badge text-bg-success">Set up</span>
                : <span className="badge text-bg-secondary">Not set up</span>}
        </ListGroup.Item>
    );
}
